import { cn } from "@v1/ui/utils";
import type { LucideIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

/** Shared keyboard-focus ring for the booking flow's raw buttons/links. */
export const BOOKING_FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-card";

/** Bordered boxes (slots, confirmation CTA, event rows) — match the 20px card. */
export const BOOKING_BOX_RADIUS_CLASS = "rounded-2xl";

/** Vertical gap for the shared icon meta-row stack (kept identical everywhere). */
export const BOOKING_META_STACK_CLASS = "space-y-3.5";

/** Form field label — AA-contrast, used across the form + custom fields. */
export const FIELD_LABEL_CLASS =
  "mb-1.5 block text-[12.5px] font-medium text-primary/70";

/** Form field input — consistent height, radius and brand focus ring. */
export const FIELD_INPUT_CLASS =
  "h-11 rounded-lg text-[14px] focus-visible:ring-2 focus-visible:ring-action-500/25 focus-visible:border-action-500/60";

/** Two-letter initials for the host avatar; falls back to a single glyph. */
function initialsFromName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return "·";
  if (parts.length === 1) return (parts[0]?.[0] ?? "·").toUpperCase();
  const first = parts[0]?.[0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return `${first}${last}`.toUpperCase();
}

/** Circular avatar with host initials in the user's personal color. */
export function BookingHostAvatar({
  name,
  colorHex,
  className,
}: {
  readonly name: string;
  /** 6-digit hex without `#` from `avatar_fallback_hex`. Falls back to neutral zinc-500. */
  readonly colorHex?: string | null;
  readonly className?: string;
}): ReactElement {
  const hex = colorHex?.replace(/^#/, "") ?? "71717a";
  return (
    <span
      aria-hidden
      style={{ backgroundColor: `#${hex}` }}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white",
        "shadow-sm ring-1 ring-inset ring-white/20",
        className,
      )}
    >
      {initialsFromName(name)}
    </span>
  );
}

/** Icon + text line used across the info panel and confirmation summary. */
export function BookingMetaRow({
  icon: Icon,
  children,
  emphasis = false,
}: {
  readonly icon: LucideIcon;
  readonly children: ReactNode;
  readonly emphasis?: boolean;
}): ReactElement {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <Icon
        className="mt-[1px] h-[18px] w-[18px] shrink-0 text-primary/40"
        strokeWidth={1.75}
      />
      <span
        className={cn(
          "min-w-0 break-words text-[13.5px] leading-snug",
          emphasis ? "font-medium text-primary/85" : "text-primary/70",
        )}
      >
        {children}
      </span>
    </div>
  );
}

/** Subtle uppercase eyebrow (org name above the event title). */
export function BookingEyebrow({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  return (
    <p className="truncate text-[11.5px] font-semibold uppercase tracking-[0.09em] text-primary/60">
      {children}
    </p>
  );
}

/** Hairline divider tuned for the light booking card. */
export function BookingDivider({
  className,
}: {
  readonly className?: string;
}): ReactElement {
  return <div className={cn("h-px w-full bg-border/60", className)} />;
}
